import FormatListBulletedOutlined from '@mui/icons-material/FormatListBulletedOutlined';
import GridViewOutlined from '@mui/icons-material/GridViewOutlined';
import {
  Box,
  Button,
  Checkbox,
  MenuItem,
  Popover,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import {
  PATH_CREATE_FROM_TEMPLATE,
  SHOW_ALL,
  SHOW_USER,
  SORT_CREATED_NEW_TO_OLD,
  SORT_CREATED_OLD_TO_NEW,
  SORT_CUSTOM,
  SORT_NAME_A_Z,
  SORT_NAME_Z_A,
  SORT_UPDATED_NEW_TO_OLD,
  SORT_UPDATED_OLD_TO_NEW,
  STATE_ALL,
  STATE_DRAFT,
  STATE_INTERNAL_PUBLISHED,
  STATE_PUBLISHED,
  STATE_UNPUBLISHED,
  STORIES_ADMIN_PRIVILEGE,
  STORIES_EDITOR_PRIVILEGE,
  STORIES_PDF_GENERATION_PRIVILEGE,
  STORIES_PUBLISHER_PRIVILEGE,
  STORIES_VIEWER_PRIVILEGE,
  TAB_ANALYTICS,
  TAB_PUBLICATIONS,
  TAB_STORIES,
  TAB_TEMPLATES,
  VIEW_GRID,
  VIEW_LIST
} from '@opengov/stories-shared/constants';
import React, { useState } from 'react';

import {
  IExtendedStoryPrivilege,
  IPublicationListMessages,
  IPublicationViewMessages,
  IPublicationVisibilityOptions,
  ISortOptions,
  IStoryListMessages,
  IStoryPrivilege,
  IStoryShowOptions,
  IStoryViewOptions,
  IStoryVisibilityOptions,
  ITabOptions
} from '../../../../types';

import { styles } from './ControlBar.styles';

export type IControlBarProps = {
  currentFolderId?: string;
  lastPath: string;
  messages: IStoryListMessages;
  publicationMessages?: IPublicationListMessages | IPublicationViewMessages;
  search: string;
  show: string;
  sort: string;
  tab: ITabOptions;
  view: string;
  storyVisibility?: string;
  publicationVisibility?: string;
  storyAccess?: IStoryPrivilege[];
  publicationView?: boolean;
  currentSubjectPublicationStoryClaim?: IExtendedStoryPrivilege | null;
  updateSearch(search: string): void;
  updateShow(show: IStoryShowOptions): void;
  updateSort(sort: ISortOptions): void;
  updateView(view: IStoryViewOptions): void;
  updatePublicationVisibility?(storyVisibility: IPublicationVisibilityOptions): void;
  updateStoryVisibility?(storyVisibility: IStoryVisibilityOptions): void;
  updateStoryAccess?(storyAccess: IStoryPrivilege[]): void;
};

export const ControlBar: React.FC<IControlBarProps> = ({
  currentFolderId,
  lastPath,
  messages,
  search,
  show,
  sort,
  publicationMessages,
  storyVisibility,
  publicationVisibility,
  storyAccess,
  tab,
  view,
  publicationView,
  currentSubjectPublicationStoryClaim,
  updateSearch,
  updateShow,
  updateSort,
  updateStoryVisibility,
  updatePublicationVisibility,
  updateStoryAccess,
  updateView
}) => {
  const [accessAnchorEl, setAccessAnchorEl] = useState<HTMLElement | null>(null);

  const placeholderText = () => {
    if (tab === TAB_TEMPLATES || lastPath === PATH_CREATE_FROM_TEMPLATE) {
      return messages.findTemplatesByName;
    } else if (tab === TAB_PUBLICATIONS) {
      return publicationMessages?.searchPublicationsByName;
    }
    return messages.findStoriesByName;
  };

  const handleAccessChange = (access: IStoryPrivilege) => {
    if (!updateStoryAccess || !storyAccess) return;
    const newAccess = storyAccess.includes(access)
      ? storyAccess.filter((item) => item !== access)
      : [...storyAccess, access];
    updateStoryAccess(newAccess);
  };

  const allAccessList: IStoryPrivilege[] = [
    STORIES_VIEWER_PRIVILEGE,
    STORIES_EDITOR_PRIVILEGE,
    STORIES_PDF_GENERATION_PRIVILEGE,
    STORIES_PUBLISHER_PRIVILEGE
  ];

  const isAllSelected = allAccessList.every((a) => storyAccess?.includes(a));

  const handleSelectAllAccess = () => {
    if (!updateStoryAccess) return;
    updateStoryAccess(isAllSelected ? [STORIES_VIEWER_PRIVILEGE] : allAccessList);
  };

  const getAccessButtonText = () => {
    if (!storyAccess || storyAccess.length === 1) return publicationMessages?.view;
    if (storyAccess.length === 4) return publicationMessages?.allViews;
    return `${storyAccess.length} ${publicationMessages?.selected}`;
  };

  const shouldShowAccessDropdown =
    tab === TAB_PUBLICATIONS &&
    publicationView &&
    currentSubjectPublicationStoryClaim &&
    !currentSubjectPublicationStoryClaim.includes(STORIES_ADMIN_PRIVILEGE);

  const searchLabel = tab === TAB_PUBLICATIONS ? publicationMessages?.search : messages.find;

  return (
    <Box sx={styles.controls}>
      {/* Search */}
      <Box sx={{ ...styles.field, ...styles.searchField }}>
        <Typography sx={styles.label}>{searchLabel}</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder={placeholderText()}
          value={search}
          onChange={(e) => updateSearch(e.target.value)}
          inputProps={{ 'data-test': 'stories-search-input', 'aria-label': searchLabel, className: 'no-format' }}
        />
      </Box>

      {/* Created By */}
      <Box sx={styles.field}>
        <Box component="label" htmlFor="stories-show-dropdown" sx={styles.srOnly}>{messages.createdBy}</Box>
        <Typography component="p" sx={styles.label} aria-hidden="true">{messages.createdBy}</Typography>
        <Select
          size="small"
          value={show}
          onChange={(e) => updateShow(e.target.value as IStoryShowOptions)}
          inputProps={{ id: 'stories-show-dropdown' }}
          data-test="stories-show-dropdown"
        >
          <MenuItem value={SHOW_ALL}>{messages.anyone}</MenuItem>
          <MenuItem value={SHOW_USER}>{messages.me}</MenuItem>
        </Select>
      </Box>

      {/* Access dropdown (publication view only) */}
      {shouldShowAccessDropdown && (
        <Box sx={styles.field}>
          <Typography sx={styles.label}>{publicationMessages?.access}</Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => setAccessAnchorEl(e.currentTarget)}
            data-test="stories-access-dropdown"
          >
            {getAccessButtonText()}
          </Button>
          <Popover
            open={Boolean(accessAnchorEl)}
            anchorEl={accessAnchorEl}
            onClose={() => setAccessAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Box component="button" sx={styles.accessSelectAll} onClick={handleSelectAllAccess}>
              {isAllSelected ? publicationMessages?.clear : publicationMessages?.selectAll}
            </Box>
            <Box sx={styles.accessItem}>
              <Checkbox size="small" checked={true} disabled />
              <Typography variant="body2" color="text.disabled">{publicationMessages?.view}</Typography>
            </Box>
            {[
              { privilege: STORIES_EDITOR_PRIVILEGE, label: publicationMessages?.edit },
              { privilege: STORIES_PDF_GENERATION_PRIVILEGE, label: publicationMessages?.export },
              { privilege: STORIES_PUBLISHER_PRIVILEGE, label: publicationMessages?.publish },
            ].map(({ privilege, label }) => (
              <Box key={privilege} sx={styles.accessItem}>
                <Checkbox size="small" checked={storyAccess?.includes(privilege as IStoryPrivilege) || false} onChange={() => handleAccessChange(privilege as IStoryPrivilege)} />
                <Typography variant="body2" sx={{ cursor: 'pointer' }}>{label}</Typography>
              </Box>
            ))}
          </Popover>
        </Box>
      )}

      {/* Story visibility */}
      {(tab === TAB_STORIES || publicationView) && lastPath !== TAB_TEMPLATES && lastPath !== PATH_CREATE_FROM_TEMPLATE && (
        <Box sx={styles.field}>
          <Box component="label" htmlFor="stories-visibility-dropdown" sx={styles.srOnly}>{messages.visibility}</Box>
          <Typography component="p" sx={styles.label} aria-hidden="true">{messages.visibility}</Typography>
          <Select
            size="small"
            value={storyVisibility}
            onChange={(e) => updateStoryVisibility?.(e.target.value as IStoryVisibilityOptions)}
            data-test="stories-visibility-dropdown"
            inputProps={{ id: 'stories-visibility-dropdown' }}
          >
            <MenuItem value={STATE_ALL}>{messages.all}</MenuItem>
            <MenuItem value={STATE_DRAFT}>{messages.drafts}</MenuItem>
            <MenuItem value={STATE_INTERNAL_PUBLISHED}>{messages.organization}</MenuItem>
            <MenuItem value={STATE_PUBLISHED}>{messages.public}</MenuItem>
          </Select>
        </Box>
      )}

      {/* Publication visibility */}
      {tab === TAB_PUBLICATIONS && publicationVisibility && (
        <Box sx={styles.field}>
          <Box component="label" htmlFor="publication-visibility-dropdown" sx={styles.srOnly}>{messages.visibility}</Box>
          <Typography component="p" sx={styles.label} aria-hidden="true">{messages.visibility}</Typography>
          <Select
            size="small"
            value={publicationVisibility}
            onChange={(e) => updatePublicationVisibility?.(e.target.value as IPublicationVisibilityOptions)}
            data-test="publicaition-visibility-dropdown"
            inputProps={{ id: 'publication-visibility-dropdown' }}
          >
            <MenuItem value={STATE_ALL}>{messages.all}</MenuItem>
            <MenuItem value={STATE_DRAFT}>{messages.drafts}</MenuItem>
            <MenuItem value={STATE_UNPUBLISHED}>{messages.unPublished}</MenuItem>
            <MenuItem value={STATE_PUBLISHED}>{messages.published}</MenuItem>
          </Select>
        </Box>
      )}

      {/* Sort */}
      <Box sx={styles.field}>
        <Box component="label" htmlFor="stories-sort-dropdown" sx={styles.srOnly}>{messages.sort}</Box>
        <Typography component="p" sx={styles.label} aria-hidden="true">{messages.sort}</Typography>
        <Select
          size="small"
          value={sort === SORT_CUSTOM && !currentFolderId ? SORT_UPDATED_NEW_TO_OLD : sort}
          onChange={(e) => updateSort(e.target.value as ISortOptions)}
          data-test="stories-sort-dropdown"
          inputProps={{ id: 'stories-sort-dropdown' }}
        >
          <MenuItem value={SORT_NAME_A_Z}>{messages.nameAZ}</MenuItem>
          <MenuItem value={SORT_NAME_Z_A}>{messages.nameZA}</MenuItem>
          <MenuItem value={SORT_CREATED_NEW_TO_OLD}>{messages.createNewtoOld}</MenuItem>
          <MenuItem value={SORT_CREATED_OLD_TO_NEW}>{messages.createOldtoNew}</MenuItem>
          <MenuItem value={SORT_UPDATED_NEW_TO_OLD}>{messages.updateNewtoOld}</MenuItem>
          <MenuItem value={SORT_UPDATED_OLD_TO_NEW}>{messages.updateOldtoNew}</MenuItem>
          {!!currentFolderId && lastPath !== TAB_PUBLICATIONS && lastPath !== PATH_CREATE_FROM_TEMPLATE && (
            <MenuItem value={SORT_CUSTOM}>{messages.customOrganized}</MenuItem>
          )}
        </Select>
      </Box>

      {/* View toggle */}
      {tab !== TAB_ANALYTICS && (
        <Box sx={styles.field}>
          <Typography sx={styles.label}>{messages.view}</Typography>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(_, val) => val && updateView(val)}
            data-test="view-button-group"

          >
            <ToggleButton value={VIEW_LIST} data-test="view-list-button" title={messages.list} sx={styles.viewButton}>
              <FormatListBulletedOutlined fontSize="small" />
            </ToggleButton>
            <ToggleButton value={VIEW_GRID} data-test="view-grid-button" title={messages.grid} sx={styles.viewButton}>
              <GridViewOutlined fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
    </Box>
  );
};
